import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { supabase } from './supabase'
import { isExtensionInstalled, relaySession, relaySignOut, relayAccountDeleted } from './extension'
import { clearSearchCache } from '../hooks/useSearch'
import { clearPreferencesCache } from './sessionCache'
import { cancelPersist, clearAllPersistedQueryCaches } from './queryClient'
import { clearSiteData } from './clearSiteData'
import { setTelemetryUser } from './telemetry'
import { setAnalyticsUser } from './analytics'
import { setViewerUserId } from './atoms/atomViewerIdentity'
import type { Session, User } from '@supabase/supabase-js'

/**
 * Session-only auth context.
 *
 * The `profile` row is no longer held here — it moved to `useProfile()` which
 * is a TanStack Query-backed hook. That split means:
 *
 *   - Session state is synchronous and cheap (never re-renders a tree when
 *     the profile is patched).
 *   - Profile fetching, caching, realtime updates, and optimistic writes live
 *     in one place (React Query + the shared `RealtimeBridge`), not spread
 *     across AuthProvider + usePreferences + a bespoke `_setProfilePreferences`
 *     escape hatch.
 *   - Adding a new profile column is a one-liner in `useProfile`, not three
 *     context edits + a reducer.
 */

interface AuthState {
  session: Session | null
  user: User | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
})

/**
 * Relay the current Supabase session to the installed extension, retrying
 * until the extension acks or the caller aborts.
 *
 * Aborts matter here: sign-out-then-sign-in (same tab, different user) fires
 * two auth state changes in quick succession. Without cancellation the first
 * user's retry loop keeps running for up to `maxAttempts * 2s` and can race
 * with the second user's relay — the extension ends up caching the wrong
 * access token.
 *
 * `signal` is also respected inside the backoff sleep so an abort takes
 * effect within milliseconds, not on the next 2s tick.
 */
async function relaySessionWithRetry(
  session: Session,
  signal: AbortSignal,
  maxAttempts = 15,
): Promise<void> {
  const sleep = (ms: number) => new Promise<void>((resolve) => {
    if (signal.aborted) return resolve()
    const t = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => { clearTimeout(t); resolve() }
    signal.addEventListener('abort', onAbort, { once: true })
  })

  for (let i = 0; i < maxAttempts; i++) {
    if (signal.aborted) return
    if (!isExtensionInstalled()) {
      await sleep(2000)
      continue
    }
    const ok = await relaySession(
      session.access_token,
      session.refresh_token ?? '',
      session.user.id,
      signal,
    )
    if (signal.aborted) return
    if (ok) return
    await sleep(2000)
  }
  console.warn('[Slash] Failed to relay session to extension after retries')
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  // Track the live relay so a new auth event (sign-in as different user,
  // sign-out, etc.) cancels the previous retry loop.
  const relayAbortRef = useRef<AbortController | null>(null)
  // Guard setState after unmount. StrictMode double-mounts the provider in
  // dev; without this the cleanup from the first mount cancels the in-flight
  // getSession(), but its `.then` still fires on a detached tree.
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    // E2E test bypass. When `VITE_E2E_BYPASS_AUTH=1` is set at build
    // time (gated by Vite's define), short-circuit Supabase auth and
    // install a deterministic fake session. This keeps Playwright
    // smoke tests independent of any staging Supabase project — the
    // tests exercise UI/routing invariants, not the auth stack.
    // Production builds never set this flag, so the branch is
    // unreachable in shipped artefacts (tree-shaken as dead code by
    // Vite when the env evaluates to `false`).
    if (import.meta.env.VITE_E2E_BYPASS_AUTH === '1') {
      const fakeSession = {
        access_token: 'e2e-fake-access',
        refresh_token: 'e2e-fake-refresh',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: {
          id: '00000000-0000-0000-0000-000000000001',
          aud: 'authenticated',
          role: 'authenticated',
          email: 'e2e@test.local',
          app_metadata: {},
          user_metadata: { full_name: 'E2E Tester' },
          created_at: new Date().toISOString(),
        },
      } as unknown as Session
      setSession(fakeSession)
      setLoading(false)
      setTelemetryUser({ id: fakeSession.user.id })
      setAnalyticsUser({ id: fakeSession.user.id })
      setViewerUserId(fakeSession.user.id)
      return () => {
        mountedRef.current = false
      }
    }

    const startRelay = (session: Session) => {
      // Cancel any previous relay loop first — last auth event wins.
      relayAbortRef.current?.abort()
      const controller = new AbortController()
      relayAbortRef.current = controller
      void relaySessionWithRetry(session, controller.signal)
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mountedRef.current) return
      setSession(session)
      setLoading(false)
      const userCtx = session?.user ? { id: session.user.id } : null
      setTelemetryUser(userCtx)
      setAnalyticsUser(userCtx)
      setViewerUserId(userCtx?.id ?? null)
      if (session) startRelay(session)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mountedRef.current) return
      setSession(session)
      setLoading(false)
      const userCtx = session?.user ? { id: session.user.id } : null
      setTelemetryUser(userCtx)
      setAnalyticsUser(userCtx)
      setViewerUserId(userCtx?.id ?? null)
      if (!session) {
        // Sign-out: cancel any pending relay so we don't leak the old token
        // to the extension after the user signs out.
        relayAbortRef.current?.abort()
        relayAbortRef.current = null
        return
      }
      if (event === 'TOKEN_REFRESHED') {
        // One-shot relay for refresh — no retry loop needed, supabase-js
        // will fire another TOKEN_REFRESHED if this one fails.
        //
        // Route the one-shot through relayAbortRef + pass the signal
        // down into relaySession so a concurrent sign-out actually
        // cancels the in-flight post. Previously the controller was
        // plumbed but `relaySession` ignored it (POST_FIX_ASTAR_AUDIT
        // finding #3); the extension could still receive a freshly-
        // minted access token after we'd told it to sign out. With the
        // signal threaded, pre-abort bypasses the postMessage entirely;
        // mid-flight abort stops the ACK wait so the relayer doesn't
        // hold a stale promise for 5s while sign-out completes.
        relayAbortRef.current?.abort()
        const controller = new AbortController()
        relayAbortRef.current = controller
        void relaySession(
          session.access_token,
          session.refresh_token ?? '',
          session.user.id,
          controller.signal,
        ).finally(() => {
          if (relayAbortRef.current === controller) relayAbortRef.current = null
        })
        return
      }
      startRelay(session)
    })

    return () => {
      mountedRef.current = false
      relayAbortRef.current?.abort()
      relayAbortRef.current = null
      subscription.unsubscribe()
    }
  }, [])

  const signOut = async () => {
    // Order matters here:
    //   0. Cancel the debounced query-cache persister FIRST, before
    //      anything else. Every step below can trigger a TanStack
    //      `updated` event that re-schedules a persist write —
    //      supabase.auth.signOut() fires auth-state-change which
    //      invalidates queries; the subsequent query transitions
    //      would otherwise land writes on localStorage AFTER step 2
    //      wiped it. Masked in practice by clearSiteData's reload
    //      (step 5), but the race window is real in code. See
    //      POST_FIX_ASTAR_AUDIT_HANDOFF finding #6.
    //   1. In-memory caches cleared synchronously (no await) so nothing
    //      running concurrently can keep them alive.
    //   2. Persisted query cache cleared — localStorage survives sign-out
    //      unless we wipe explicitly. See ADR 0019.
    //   3. Supabase sign-out fires the /auth/v1/logout POST and drops its
    //      own session-storage keys.
    //   4. Extension relay tells the overlay to tear down.
    //   5. Full site-data nuke — cookies + all localStorage + all IDBs +
    //      caches + service workers. The included `location.replace('/')`
    //      lands the user on a clean login page. See ADR 0020.
    cancelPersist()
    clearSearchCache()
    const userId = session?.user?.id
    if (userId) clearPreferencesCache(userId)
    clearAllPersistedQueryCaches()
    try {
      await supabase.auth.signOut()
    } catch (err) {
      console.error('[Slash] signOut failed:', (err as Error).message)
    }
    relaySignOut()
    await clearSiteData()
  }

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user ?? null,
      loading,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

// Re-export for callers that need to trigger the extension account-deleted relay
export { relayAccountDeleted }

// Re-export Profile type from its new home for convenience — saves call sites
// updating every import.
export type { Profile } from '../hooks/useProfile'
